<template>
  <teleport :to="teleportTarget">
    <AppTransitionFade :duration="duration">
      <div
        v-if="isVisible"
        ref="floating"
        :style="floatingStyles"
        class="fixed top-0 left-0 z-[1000]"
      >
        <slot />
      </div>
    </AppTransitionFade>
  </teleport>
</template>

<script setup lang="ts">
import {
  useFloating,
  offset as offsetMiddleware,
  flip as flipMiddleware,
  shift as shiftMiddleware,
  autoUpdate as autoUpdateMiddleware,
  type VirtualElement,
} from '@floating-ui/vue'

export type HeadlessFloatingWithAnchorPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'bottom-start'
  | 'left-start'
  | 'right-start'
  | 'top-end'
  | 'bottom-end'
  | 'left-end'
  | 'right-end'

export interface HeadlessFloatingWithAnchorProps {
  anchorReference: HTMLElement | VirtualElement | null
  offset?: number
  flip?: boolean
  shift?: boolean
  autoUpdate?: boolean
  placement?: HeadlessFloatingWithAnchorPlacement
  isVisible?: boolean
  duration?: string
  shiftPadding?: number
}

const props = withDefaults(defineProps<HeadlessFloatingWithAnchorProps>(), {
  offset: 0,
  flip: true,
  shift: true,
  autoUpdate: true,
  placement: 'bottom',
  isVisible: true,
  duration: '0.15',
  shiftPadding: 0,
})

const reactiveAnchorReference = computed(() => props.anchorReference || null)

/**
 * Where the floating element is mounted. `body` by default, but an open
 * `<dialog>` (the app's modals, shown with `showModal()`) sits in the
 * browser's top layer, and nothing appended to `body` can paint above the top
 * layer whatever its z-index. So while a dialog is open the element goes
 * inside it, where `position: fixed` still measures against the viewport.
 *
 * Resolved when the element is about to show, and again whenever a dialog
 * opens or closes while it is showing: a menu left open as its modal closes
 * would otherwise stay inside the closed dialog, and one open as a modal
 * opens would stay on `body` under it, until it was hidden and shown again.
 */
const teleportTarget = ref<HTMLElement | string>('body')

/**
 * Which open dialog the element belongs in, if any.
 *
 * The one its anchor sits inside, first: a menu belongs with the button that
 * opened it, whichever dialog that is. Otherwise the dialogs actually shown as
 * modals (`:modal`, the ones in the top layer), and only then any open dialog.
 * Page order is the tiebreak in both, which is right for the one-modal-at-a-
 * time app this is, though the top layer's own order (the order `showModal`
 * was called in) is not something the page can read.
 */
function findDialogTarget(): HTMLElement | null {
  const anchor = props.anchorReference
  if (anchor instanceof Element) {
    const own = anchor.closest<HTMLDialogElement>('dialog[open]')
    if (own) return own
  }

  let modals: NodeListOf<HTMLDialogElement> | null = null
  try {
    modals = document.querySelectorAll<HTMLDialogElement>('dialog:modal')
  } catch {
    // A browser (or test DOM) without the `:modal` selector.
  }
  if (modals && modals.length > 0) return modals[modals.length - 1]

  const open = document.querySelectorAll<HTMLDialogElement>('dialog[open]')
  return open[open.length - 1] ?? null
}

function resolveTeleportTarget() {
  if (!props.isVisible || typeof document === 'undefined') return
  const target = findDialogTarget() ?? 'body'
  if (teleportTarget.value !== target) teleportTarget.value = target
}

watch(() => props.isVisible, resolveTeleportTarget, { immediate: true })

// Watched only while visible: a hidden element's target is resolved on show.
useMutationObserver(
  () =>
    props.isVisible && typeof document !== 'undefined' ? document.body : null,
  resolveTeleportTarget,
  {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open'],
  },
)

const placementReactive = computed(() => props.placement || 'bottom')

const floating = useTemplateRef('floating')

const middleware = computed(() => {
  const middleware = []

  if (props.offset) {
    middleware.push(offsetMiddleware(props.offset))
  }

  if (props.flip) {
    middleware.push(flipMiddleware())
  }

  if (props.shift) {
    middleware.push(shiftMiddleware({ padding: props.shiftPadding ?? 0 }))
  }

  return middleware
})

const { floatingStyles } = useFloating(reactiveAnchorReference, floating, {
  placement: placementReactive,
  strategy: 'fixed',
  middleware,
  whileElementsMounted: props.autoUpdate ? autoUpdateMiddleware : undefined,
})

defineExpose({ floating })
</script>
